import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Fixture, FixturesState } from '@/types/fixtures';
import { FixturesService } from '@/services/fixturesService';
import { groupFixturesByDate, sortFixturesByDate, isValidFixture } from '@/utils/fixtureUtils';

/**
 * Fixtures Store State Interface with Actions
 */
interface FixturesStoreState extends FixturesState {
  // Actions
  fetchFixtures: (refresh?: boolean, isLoadMore?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  refreshFixtures: () => Promise<void>;
  setSelectedFixture: (fixture: Fixture | null) => void;
  setSelectedFixtureById: (fixtureId: number | null) => void;
  clearError: () => void;
  clearCache: () => void;
  searchFixtures: (query: string) => Promise<void>;
  filterFixtures: (filters: {
    status?: string[];
    trade?: string[];
    dateFrom?: string;
    dateTo?: string;
  }) => Promise<void>;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: FixturesState = {
  fixtures: [],
  groupedFixtures: {},
  loading: false,
  loadingMore: false,
  error: null,
  hasMore: true,
  page: 1,
  refreshing: false,
  selectedFixture: null,
};

const PAGE_SIZE = 50;

/**
 * Helper function to validate and clean cached fixtures
 */
function validateCachedFixtures(fixtures: unknown[]): Fixture[] {
  return fixtures.filter(isValidFixture);
}

/**
 * Fixtures Store
 */
export const useFixturesStore = create<FixturesStoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Fetch fixtures with optional refresh
       */
      fetchFixtures: async (refresh = false, isLoadMore = false) => {
        const { fixtures, page, loading, loadingMore } = get();

        // Prevent multiple simultaneous requests
        if ((loading || loadingMore) && !refresh) {
          return;
        }

        if (refresh) {
          set({ loading: true, error: null, page: 1, loadingMore: false });
        } else if (fixtures.length === 0) {
          set({ loading: true, error: null, loadingMore: false });
        } else if (isLoadMore) {
          set({ loadingMore: true, error: null });
        }

        try {
          const targetPage = isLoadMore ? page : 1;
          const response = await FixturesService.getFixtures(targetPage, PAGE_SIZE);

          // Validate fetched data
          const validFixtures = validateCachedFixtures(response.data);

          let newFixtures: Fixture[];
          if (isLoadMore) {
            const existingIds = new Set(fixtures.map((f) => f.fixtureId));
            const uniqueNewFixtures = validFixtures.filter((f) => !existingIds.has(f.fixtureId));
            newFixtures = [...fixtures, ...uniqueNewFixtures];
          } else {
            newFixtures = validFixtures;
          }

          // Sort and group fixtures
          const sortedFixtures = sortFixturesByDate(newFixtures);
          const grouped = groupFixturesByDate(sortedFixtures);

          set({
            fixtures: sortedFixtures,
            groupedFixtures: grouped,
            loading: false,
            loadingMore: false,
            hasMore: response.hasMore && response.data.length === PAGE_SIZE,
            page: isLoadMore ? page + 1 : 2,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch fixtures';
          set({
            loading: false,
            loadingMore: false,
            error: errorMessage,
          });
          console.error('Error fetching fixtures:', error);
        }
      },

      /**
       * Load more fixtures (pagination)
       */
      loadMore: async () => {
        const { hasMore, loading, loadingMore } = get();

        if (!hasMore || loading || loadingMore) {
          return;
        }

        await get().fetchFixtures(false, true);
      },

      /**
       * Refresh fixtures (force refresh from server)
       */
      refreshFixtures: async () => {
        set({ refreshing: true });

        try {
          await get().fetchFixtures(true);
        } finally {
          set({ refreshing: false });
        }
      },

      /**
       * Set selected fixture for details view
       */
      setSelectedFixture: (fixture: Fixture | null) => {
        set({ selectedFixture: fixture });
      },

      setSelectedFixtureById: (fixtureId: number | null) => {
        if (!fixtureId) {
          set({ selectedFixture: null });
          return;
        }

        const fixture = get().fixtures.find((item) => item.fixtureId === fixtureId) || null;
        set({ selectedFixture: fixture });
      },

      /**
       * Search fixtures
       */
      searchFixtures: async (query: string) => {
        if (!query.trim()) {
          // If empty query, fetch all fixtures
          await get().fetchFixtures(true);
          return;
        }

        set({ loading: true, error: null });

        try {
          const results = await FixturesService.searchFixtures(query, 100);
          const validResults = validateCachedFixtures(results);
          const sortedResults = sortFixturesByDate(validResults);
          const grouped = groupFixturesByDate(sortedResults);

          set({
            fixtures: sortedResults,
            groupedFixtures: grouped,
            loading: false,
            hasMore: false, // Search results don't have pagination
            page: 1,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Search failed';
          set({
            loading: false,
            error: errorMessage,
          });
        }
      },

      /**
       * Filter fixtures
       */
      filterFixtures: async (filters: {
        status?: string[];
        trade?: string[];
        dateFrom?: string;
        dateTo?: string;
      }) => {
        set({ loading: true, error: null, page: 1 });

        try {
          const response = await FixturesService.getFilteredFixtures({
            pageNumber: 1,
            pageSize: PAGE_SIZE,
            ...filters,
          });

          const validFixtures = validateCachedFixtures(response.data);
          const sortedFixtures = sortFixturesByDate(validFixtures);
          const grouped = groupFixturesByDate(sortedFixtures);

          set({
            fixtures: sortedFixtures,
            groupedFixtures: grouped,
            loading: false,
            hasMore: response.hasMore,
            page: 2,
            error: null,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Filter failed';
          set({
            loading: false,
            error: errorMessage,
          });
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Clear cache and reset
       */
      clearCache: () => {
        set({ ...initialState });
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set({ ...initialState });
      },
    }),
    {
      name: 'FixturesStore',
    }
  )
);

/**
 * Selector hooks for better performance
 */
export const useFixturesLoading = () => useFixturesStore((state) => state.loading);
export const useFixturesLoadingMore = () => useFixturesStore((state) => state.loadingMore);
export const useFixturesError = () => useFixturesStore((state) => state.error);
export const useFixturesData = () =>
  useFixturesStore((state) => ({
    fixtures: state.fixtures,
    groupedFixtures: state.groupedFixtures,
    hasMore: state.hasMore,
  }));
export const useSelectedFixture = () => useFixturesStore((state) => state.selectedFixture);
